import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Heart, ShoppingBag, Star, Truck, Shield, RotateCcw, ChevronDown, ChevronUp, Minus, Plus } from 'lucide-react';
import Header from './components/Header23';
import Footer from './components/Footer23';

const Template23ProductDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [selectedSize, setSelectedSize] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [activeTab, setActiveTab] = useState('description');
    const [wishlisted, setWishlisted] = useState(false);
    const [selectedImage, setSelectedImage] = useState(0);
    const [expandedSection, setExpandedSection] = useState('fabric');

    const product = {
        id: 1,
        name: 'Bridal Silk Lehenga',
        price: 45999,
        originalPrice: 52999,
        occasion: ['Wedding', 'Sangeet'],
        description: 'Exquisitely handcrafted bridal silk lehenga featuring intricate zardozi embroidery with gold and silver threads. This masterpiece blends traditional Indian artistry with contemporary silhouettes, perfect for making a grand entrance at your wedding celebration.',
        fabric: 'Pure Banarasi Silk with Zardozi & Sequin Embroidery',
        lining: 'Premium Cotton Silk',
        care: 'Dry Clean Only',
        weight: '3.5 kg (approx)',
        includes: 'Lehenga, Blouse (Unstitched), Dupatta',
        sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'Custom'],
        images: [
            'https://images.pexels.com/photos/8881954/pexels-photo-8881954.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/9419108/pexels-photo-9419108.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/7349020/pexels-photo-7349020.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/9419032/pexels-photo-9419032.jpeg?auto=compress&cs=tinysrgb&w=800',
        ],
        rating: 4.8,
        reviews: 124,
        sku: 'UTB-LH-001',
    };

    const crossSellProducts = [
        {
            id: 2,
            name: 'Sangeet Anarkali Set',
            price: 18500,
            image: 'https://images.pexels.com/photos/7123307/pexels-photo-7123307.jpeg?auto=compress&cs=tinysrgb&w=800',
            occasion: 'Sangeet',
        },
        {
            id: 3,
            name: 'Festive Zari Saree',
            price: 12999,
            image: 'https://images.pexels.com/photos/2723623/pexels-photo-2723623.jpeg?auto=compress&cs=tinysrgb&w=800',
            occasion: 'Festive',
        },
        {
            id: 6,
            name: 'Reception Gown',
            price: 22500,
            image: 'https://images.pexels.com/photos/9419108/pexels-photo-9419108.jpeg?auto=compress&cs=tinysrgb&w=800',
            occasion: 'Reception',
        },
        {
            id: 8,
            name: 'Eid Special Gharara',
            price: 14999,
            image: 'https://images.pexels.com/photos/9901808/pexels-photo-9901808.jpeg?auto=compress&cs=tinysrgb&w=800',
            occasion: 'Festive',
        },
    ];

    const fabricDetails = [
        { label: 'Fabric', value: product.fabric },
        { label: 'Lining', value: product.lining },
        { label: 'Care', value: product.care },
        { label: 'Weight', value: product.weight },
        { label: 'Set Includes', value: product.includes },
    ];

    const toggleSection = (section) => {
        setExpandedSection(expandedSection === section ? '' : section);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Nunito+Sans:wght@300;400;500;600;700&display=swap');

                .utsav-detail-layout {
                    display: flex;
                    flex-direction: column;
                    gap: 40px;
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 120px 24px 80px;
                }
                @media (min-width: 768px) {
                    .utsav-detail-layout {
                        flex-direction: row;
                        gap: 60px;
                        padding: 140px 24px 80px;
                    }
                }
                .utsav-gallery {
                    flex: 1;
                }
                .utsav-info {
                    flex: 1;
                    max-width: 100%;
                }
                @media (min-width: 768px) {
                    .utsav-info {
                        max-width: 520px;
                    }
                }
                .utsav-thumb-row {
                    display: flex;
                    gap: 10px;
                    margin-top: 12px;
                }
                .utsav-cross-sell-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 16px;
                }
                @media (min-width: 768px) {
                    .utsav-cross-sell-grid {
                        grid-template-columns: repeat(4, 1fr);
                        gap: 24px;
                    }
                }
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#FFFDF8' }}>
                <Header cartCount={0} />

                {/* Breadcrumb */}
                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    padding: '100px 24px 0',
                }}>
                    <p style={{
                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                        fontSize: '13px',
                        color: '#5A5A5A',
                        margin: 0,
                    }}>
                        <span style={{ cursor: 'pointer' }} onClick={() => navigate('/template23/shop')}>Shop</span>
                        <span style={{ margin: '0 8px', color: '#E8E0D4' }}>/</span>
                        <span style={{ color: '#1B3A2D' }}>{product.name}</span>
                    </p>
                </div>

                {/* Product Layout */}
                <div className="utsav-detail-layout" style={{ paddingTop: '24px' }}>
                    {/* Gallery */}
                    <div className="utsav-gallery">
                        <div style={{
                            borderRadius: '16px',
                            overflow: 'hidden',
                            backgroundColor: '#F8F3EC',
                            aspectRatio: '3/4',
                        }}>
                            <img
                                src={product.images[selectedImage]}
                                alt={product.name}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />
                        </div>
                        <div className="utsav-thumb-row">
                            {product.images.map((img, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => setSelectedImage(idx)}
                                    style={{
                                        flex: 1,
                                        aspectRatio: '1',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        border: selectedImage === idx ? '2px solid #C5974B' : '2px solid transparent',
                                        opacity: selectedImage === idx ? 1 : 0.6,
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    <img
                                        src={img}
                                        alt={`View ${idx + 1}`}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Product Info */}
                    <div className="utsav-info">
                        {/* Occasion Badges */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                            {product.occasion.map(occ => (
                                <span key={occ} style={{
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    letterSpacing: '1px',
                                    textTransform: 'uppercase',
                                    color: '#1B3A2D',
                                    backgroundColor: '#F8F3EC',
                                    border: '1px solid #E8E0D4',
                                    padding: '4px 12px',
                                    borderRadius: '4px',
                                }}>
                                    Perfect for: {occ}
                                </span>
                            ))}
                        </div>

                        <h1 style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            fontSize: 'clamp(24px, 3vw, 36px)',
                            fontWeight: 700,
                            color: '#1B3A2D',
                            margin: '0 0 8px 0',
                            lineHeight: 1.2,
                        }}>
                            {product.name}
                        </h1>

                        {/* Rating */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', gap: '2px' }}>
                                {[1, 2, 3, 4, 5].map(star => (
                                    <Star
                                        key={star}
                                        size={14}
                                        fill={star <= Math.floor(product.rating) ? '#C5974B' : 'none'}
                                        color="#C5974B"
                                    />
                                ))}
                            </div>
                            <span style={{
                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                fontSize: '13px',
                                color: '#5A5A5A',
                            }}>
                                {product.rating} ({product.reviews} reviews)
                            </span>
                        </div>

                        {/* Price */}
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '24px' }}>
                            <span style={{
                                fontFamily: "'Libre Baskerville', Georgia, serif",
                                fontSize: '28px',
                                fontWeight: 700,
                                color: '#C5974B',
                            }}>
                                {'\u20B9'}{product.price.toLocaleString('en-IN')}
                            </span>
                            <span style={{
                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                fontSize: '16px',
                                color: '#5A5A5A',
                                textDecoration: 'line-through',
                            }}>
                                {'\u20B9'}{product.originalPrice.toLocaleString('en-IN')}
                            </span>
                            <span style={{
                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                fontSize: '13px',
                                fontWeight: 700,
                                color: '#1B3A2D',
                                backgroundColor: '#E8F5E9',
                                padding: '2px 10px',
                                borderRadius: '4px',
                            }}>
                                {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
                            </span>
                        </div>

                        {/* Description */}
                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '15px',
                            color: '#5A5A5A',
                            lineHeight: 1.7,
                            margin: '0 0 28px 0',
                        }}>
                            {product.description}
                        </p>

                        {/* Size Selector */}
                        <div style={{ marginBottom: '24px' }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '12px',
                            }}>
                                <span style={{
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#1B3A2D',
                                }}>
                                    Select Size
                                </span>
                                <span style={{
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '13px',
                                    color: '#C5974B',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                }}>
                                    Size Guide
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                {product.sizes.map(size => (
                                    <button
                                        key={size}
                                        onClick={() => setSelectedSize(size)}
                                        style={{
                                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                            fontSize: '13px',
                                            fontWeight: selectedSize === size ? 700 : 500,
                                            padding: size === 'Custom' ? '10px 20px' : '10px 16px',
                                            backgroundColor: selectedSize === size ? '#1B3A2D' : '#FFFFFF',
                                            color: selectedSize === size ? '#FFFFFF' : '#1B3A2D',
                                            border: selectedSize === size ? '1px solid #1B3A2D' : '1px solid #E8E0D4',
                                            borderRadius: size === 'Custom' ? '8px' : '8px',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            minWidth: '44px',
                                        }}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Quantity */}
                        <div style={{ marginBottom: '28px' }}>
                            <span style={{
                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#1B3A2D',
                                display: 'block',
                                marginBottom: '12px',
                            }}>
                                Quantity
                            </span>
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                border: '1px solid #E8E0D4',
                                borderRadius: '8px',
                                overflow: 'hidden',
                            }}>
                                <button
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    style={{
                                        width: '44px',
                                        height: '44px',
                                        backgroundColor: '#F8F3EC',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Minus size={16} color="#1B3A2D" />
                                </button>
                                <span style={{
                                    width: '50px',
                                    textAlign: 'center',
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    color: '#1B3A2D',
                                }}>
                                    {quantity}
                                </span>
                                <button
                                    onClick={() => setQuantity(quantity + 1)}
                                    style={{
                                        width: '44px',
                                        height: '44px',
                                        backgroundColor: '#F8F3EC',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Plus size={16} color="#1B3A2D" />
                                </button>
                            </div>
                        </div>

                        {/* CTA Buttons */}
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '28px' }}>
                            <button
                                style={{
                                    flex: 1,
                                    padding: '16px 32px',
                                    backgroundColor: '#C5974B',
                                    color: '#FFFFFF',
                                    border: 'none',
                                    borderRadius: '10px',
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    letterSpacing: '1px',
                                    textTransform: 'uppercase',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    transition: 'all 0.3s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#B8873F';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#C5974B';
                                }}
                            >
                                <ShoppingBag size={18} />
                                Add to Cart
                            </button>
                            <button
                                onClick={() => setWishlisted(!wishlisted)}
                                style={{
                                    width: '52px',
                                    height: '52px',
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E8E0D4',
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                <Heart
                                    size={20}
                                    fill={wishlisted ? '#7A1B3B' : 'none'}
                                    color={wishlisted ? '#7A1B3B' : '#1B3A2D'}
                                />
                            </button>
                        </div>

                        {/* Trust Badges */}
                        <div style={{
                            display: 'flex',
                            gap: '24px',
                            padding: '20px 0',
                            borderTop: '1px solid #E8E0D4',
                            borderBottom: '1px solid #E8E0D4',
                            marginBottom: '28px',
                            flexWrap: 'wrap',
                        }}>
                            {[
                                { icon: <Truck size={18} />, text: 'Free Shipping' },
                                { icon: <Shield size={18} />, text: 'Authentic Craft' },
                                { icon: <RotateCcw size={18} />, text: '7-Day Returns' },
                            ].map((badge, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <div style={{ color: '#1B3A2D' }}>{badge.icon}</div>
                                    <span style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        color: '#5A5A5A',
                                    }}>
                                        {badge.text}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Expandable Fabric Details */}
                        <div style={{ marginBottom: '24px' }}>
                            <button
                                onClick={() => toggleSection('fabric')}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '16px 0',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    borderBottom: '1px solid #E8E0D4',
                                    cursor: 'pointer',
                                }}
                            >
                                <span style={{
                                    fontFamily: "'Libre Baskerville', Georgia, serif",
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    color: '#1B3A2D',
                                }}>
                                    Fabric & Details
                                </span>
                                {expandedSection === 'fabric'
                                    ? <ChevronUp size={18} color="#C5974B" />
                                    : <ChevronDown size={18} color="#C5974B" />
                                }
                            </button>
                            {expandedSection === 'fabric' && (
                                <div style={{ padding: '16px 0' }}>
                                    {fabricDetails.map((detail, idx) => (
                                        <div key={idx} style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            padding: '8px 0',
                                            borderBottom: idx < fabricDetails.length - 1 ? '1px solid #F8F3EC' : 'none',
                                        }}>
                                            <span style={{
                                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                color: '#1B3A2D',
                                            }}>
                                                {detail.label}
                                            </span>
                                            <span style={{
                                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                                fontSize: '13px',
                                                color: '#5A5A5A',
                                                textAlign: 'right',
                                                maxWidth: '60%',
                                            }}>
                                                {detail.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Shipping Info */}
                        <div>
                            <button
                                onClick={() => toggleSection('shipping')}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '16px 0',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    borderBottom: '1px solid #E8E0D4',
                                    cursor: 'pointer',
                                }}
                            >
                                <span style={{
                                    fontFamily: "'Libre Baskerville', Georgia, serif",
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    color: '#1B3A2D',
                                }}>
                                    Shipping & Delivery
                                </span>
                                {expandedSection === 'shipping'
                                    ? <ChevronUp size={18} color="#C5974B" />
                                    : <ChevronDown size={18} color="#C5974B" />
                                }
                            </button>
                            {expandedSection === 'shipping' && (
                                <div style={{ padding: '16px 0' }}>
                                    <p style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '14px',
                                        color: '#5A5A5A',
                                        lineHeight: 1.7,
                                        margin: 0,
                                    }}>
                                        Standard delivery: 7-10 business days. Express delivery available for wedding date guarantees.
                                        Free shipping on all orders above {'\u20B9'}5,000.
                                        Custom-sized orders may take an additional 5-7 business days.
                                    </p>
                                </div>
                            )}
                        </div>

                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '12px',
                            color: '#5A5A5A',
                            marginTop: '24px',
                        }}>
                            SKU: {product.sku}
                        </p>
                    </div>
                </div>

                {/* Complete the Look - Cross Sell */}
                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    padding: '0 24px 80px',
                }}>
                    <div style={{
                        textAlign: 'center',
                        marginBottom: '40px',
                    }}>
                        <div style={{
                            width: '40px',
                            height: '2px',
                            backgroundColor: '#C5974B',
                            margin: '0 auto 16px',
                        }} />
                        <h2 style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            fontSize: 'clamp(22px, 3vw, 32px)',
                            fontWeight: 700,
                            color: '#1B3A2D',
                            margin: '0 0 8px 0',
                        }}>
                            Complete the Look
                        </h2>
                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '15px',
                            color: '#5A5A5A',
                            margin: 0,
                        }}>
                            Curated pieces to complement your celebration ensemble
                        </p>
                    </div>

                    <div className="utsav-cross-sell-grid">
                        {crossSellProducts.map(item => (
                            <div
                                key={item.id}
                                onClick={() => navigate(`/template23/product/${item.id}`)}
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    boxShadow: '0 2px 12px rgba(27, 58, 45, 0.06)',
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                <div style={{
                                    aspectRatio: '3/4',
                                    overflow: 'hidden',
                                    backgroundColor: '#F8F3EC',
                                }}>
                                    <img
                                        src={item.image}
                                        alt={item.name}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            transition: 'transform 0.6s ease',
                                        }}
                                    />
                                </div>
                                <div style={{ padding: '14px' }}>
                                    <p style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        color: '#C5974B',
                                        letterSpacing: '1.5px',
                                        textTransform: 'uppercase',
                                        margin: '0 0 4px 0',
                                    }}>
                                        {item.occasion}
                                    </p>
                                    <h4 style={{
                                        fontFamily: "'Libre Baskerville', Georgia, serif",
                                        fontSize: '14px',
                                        fontWeight: 400,
                                        color: '#1B3A2D',
                                        margin: '0 0 6px 0',
                                    }}>
                                        {item.name}
                                    </h4>
                                    <p style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '15px',
                                        fontWeight: 700,
                                        color: '#C5974B',
                                        margin: 0,
                                    }}>
                                        {'\u20B9'}{item.price.toLocaleString('en-IN')}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default Template23ProductDetail;
